// DashboardHome.js
import React, { useState, useEffect } from 'react';
import { getAllUsers } from '../../api/users';
import { getAllEvents } from '../../api/events';
import { getAllVolunteers } from '../../api/volunteers';
import { getAllConferences } from '../../api/conferences';
import './DashboardHome.css';

const DashboardHome = () => {
  const [stats, setStats] = useState({
    totalMembers: 0,
    totalEvents: 0,
    totalVolunteers: 0,
    upcomingEvents: 0,
    totalPoints: 0,
    activeMembers: 0,
    totalConferences: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const [users, events, volunteers, conferences] = await Promise.all([
        getAllUsers(),
        getAllEvents(),
        getAllVolunteers(),
        getAllConferences()
      ]);

      // Calculate statistics
      const now = new Date();
      const upcomingEvents = events.filter(event => new Date(event.date) >= now).length;
      const totalPoints = users.reduce((sum, user) => sum + (user.points || 0), 0);
      const activeMembers = users.filter(user => (user.points || 0) > 0).length;

      setStats({
        totalMembers: users.length,
        totalEvents: events.length,
        totalVolunteers: volunteers.length,
        upcomingEvents,
        totalPoints,
        activeMembers,
        totalConferences: conferences.length
      });

      // Generate recent activity (last 5 events)
      const sortedEvents = [...events]
        .sort((a, b) => new Date(b.createdAt?.seconds * 1000) - new Date(a.createdAt?.seconds * 1000))
        .slice(0, 5);
      
      setRecentActivity(sortedEvents.map(event => ({
        type: 'event',
        title: event.title,
        date: event.createdAt?.seconds ? new Date(event.createdAt.seconds * 1000) : new Date()
      })));

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="dashboard-home">
        <div className="loading-spinner">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-home">
      <div className="dashboard-header">
        <h1>Admin Dashboard</h1>
        <p>Welcome back! Here's what's happening with your organization.</p>
      </div>

      <div className="dashboard-stats">
        <div className="stat-card stat-primary">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h3>Total Members</h3>
            <p className="stat-number">{stats.totalMembers}</p>
            <span className="stat-label">{stats.activeMembers} active</span>
          </div>
        </div>

        <div className="stat-card stat-success">
          <div className="stat-icon">📅</div>
          <div className="stat-content">
            <h3>Total Events</h3>
            <p className="stat-number">{stats.totalEvents}</p>
            <span className="stat-label">{stats.upcomingEvents} upcoming</span>
          </div>
        </div>

        <div className="stat-card stat-info">
          <div className="stat-icon">🤝</div>
          <div className="stat-content">
            <h3>Volunteers</h3>
            <p className="stat-number">{stats.totalVolunteers}</p>
            <span className="stat-label">registered</span>
          </div>
        </div>

        <div className="stat-card stat-warning">
          <div className="stat-icon">⭐</div>
          <div className="stat-content">
            <h3>Total Points</h3>
            <p className="stat-number">{stats.totalPoints}</p>
            <span className="stat-label">awarded</span>
          </div>
        </div>

        <div className="stat-card stat-primary" style={{ borderLeft: '4px solid var(--color-accent)' }}>
          <div className="stat-icon">🎓</div>
          <div className="stat-content">
            <h3>Conferences</h3>
            <p className="stat-number">{stats.totalConferences}</p>
            <span className="stat-label">active programs</span>
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="dashboard-section">
          <h2>Recent Activity</h2>
          <div className="activity-list">
            {recentActivity.length === 0 ? (
              <p className="no-activity">No recent activity</p>
            ) : (
              recentActivity.map((activity, index) => (
                <div key={index} className="activity-item">
                  <div className="activity-icon">
                    {activity.type === 'event' ? '📅' : '👤'}
                  </div>
                  <div className="activity-details">
                    <p className="activity-title">{activity.title}</p>
                    <p className="activity-date">{formatDate(activity.date)}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="dashboard-section">
          <h2>Quick Actions</h2>
          <div className="quick-actions">
            <a href="/admin/manage-events" className="action-card">
              <div className="action-icon">➕</div>
              <p>Create Event</p>
            </a>
            <a href="/admin/manage-members" className="action-card">
              <div className="action-icon">👤</div>
              <p>Manage Members</p>
            </a>
            <a href="/admin/manage-conferences" className="action-card">
              <div className="action-icon">🎓</div>
              <p>Conferences</p>
            </a>
            <a href="/admin/manage-volunteers" className="action-card">
              <div className="action-icon">🤝</div>
              <p>View Volunteers</p>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
